import React, { useState, useEffect } from 'react';
import { 
    Container, Typography, Box, TextField, Button, Alert, Grid, Paper, 
    FormControl, InputLabel, Select, MenuItem, useTheme, IconButton, CircularProgress,
    Dialog, DialogTitle, DialogContent, List, ListItem, ListItemButton, ListItemText, Chip
} from '@mui/material';
import { ArrowBack, Delete, Save, CheckCircle, Link as LinkIcon, ContentCopy, Email, AccessTime, Star } from '@mui/icons-material';
import api from '../../api/axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { parseUtc } from '../../utils/time';


const JobForm = () => {
    const theme = useTheme();
    const { id } = useParams(); // Get ID from URL if present
    const [searchParams] = useSearchParams();
    const queryClientId = searchParams.get('client_id');
    const isEditMode = !!id;
    
    const [formData, setFormData] = useState({
        description: '',
        location: '',
        scheduled_date: '',
        scheduled_time: '',
        client_id: '',
        status: 'Needs Info',
        // New Fields
        job_type: 'standard',
        urgency: 'normal',
        site_access: 'arrange',
        site_contact_name: '',
        site_contact_phone: '',
        bill_to_other: false,
        billing_name: '',
        billing_email: '',
        flexibility: 'flexible',
        not_before_date: '',
        time_preference: 'any',
        special_instructions: '',
        employee_id: '',
        failure_reason: '',
        cancellation_reason: ''
    });
    const [clients, setClients] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [magicLink, setMagicLink] = useState(null);
    const [linkLoading, setLinkLoading] = useState(false);
    
    // Suggestion State
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    
    const navigate = useNavigate();

    useEffect(() => {
        const loadData = async () => {
            if (isEditMode) setInitialLoading(true);
            try {
                // Fetch clients and employees
                const [clientsRes, employeesRes] = await Promise.all([
                    api.get('/admin/clients').catch(() => ({ data: [] })),
                    api.get('/admin/employees').catch(() => ({ data: [] }))
                ]);
                setClients(clientsRes.data);
                setEmployees(employeesRes.data);

                // Fetch job details if editing
                if (isEditMode) {
                    const jobRes = await api.get(`/admin/jobs/${id}`);
                    const job = jobRes.data;
                    
                    // Parse date and time
                    let date = '';
                    let time = '';
                    if (job.scheduled_date) {
                        const dt = new Date(job.scheduled_date);
                        date = dt.toISOString().split('T')[0];
                        time = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                    }

                    setFormData({
                        description: job.description || '',
                        location: job.location || '',
                        scheduled_date: date,
                        scheduled_time: time,
                        client_id: job.client_id || '',
                        status: job.status || 'Needs Info',
                        job_type: job.job_type || 'standard',
                        urgency: job.urgency || 'normal',
                        site_access: job.site_access || 'arrange',
                        site_contact_name: job.site_contact_name || '',
                        site_contact_phone: job.site_contact_phone || '',
                        bill_to_other: job.bill_to_other || false,
                        billing_name: job.billing_name || '',
                        billing_email: job.billing_email || '',
                        flexibility: job.flexibility || 'flexible',
                        not_before_date: job.not_before_date || '',
                        time_preference: job.time_preference || 'any',
                        special_instructions: job.special_instructions || '',
                        employee_id: job.employee_id || '',
                        failure_reason: job.failure_reason || '',
                        cancellation_reason: job.cancellation_reason || ''
                    });
                }
            } catch (err) {
                console.error('Failed to load data', err);
                setError('Failed to load job details');
            }
            setInitialLoading(false);
        };

        loadData();
    }, [id, isEditMode]);

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({
            ...formData,
            [e.target.name]: value
        });
    };

    const handleGenerateLink = async (sendEmail = false) => {
        setLinkLoading(true);
        setMagicLink(null);
        try {
            // Find client email if we want to send it
            let clientEmail = null;
            if (sendEmail && formData.client_id) {
                const client = clients.find(c => c.id === formData.client_id);
                if (client) clientEmail = client.email;
            }

            const payload = sendEmail ? { email: clientEmail } : {};
            const res = await api.post(`/admin/jobs/${id}/link`, payload);
            setMagicLink(res.data.magic_link);
            
            if (res.data.email_sent) {
                setSuccess('Magic link generated and sent to client!');
            } else if (sendEmail && !res.data.email_sent) {
                // Tried to send but failed (or no email)
                 setSuccess('Magic link generated (Email not sent - check client email)');
            } else {
                 setSuccess('Magic link generated!');
            }
        } catch (err) {
            console.error(err);
            setError('Failed to generate magic link');
        }
        setLinkLoading(false);
    };

    const handleSuggestTimes = async () => {
        if (!formData.location) {
            setError("Please enter a location to get scheduling suggestions");
            return;
        }
        setSuggestionsLoading(true);
        setError(null);
        try {
            const res = await api.post('/admin/jobs/best-times', {
                location: formData.location,
                duration_mins: 60, // Default duration
                days_to_check: 14
            });
            
            if (!res.data.suggestions || res.data.suggestions.length === 0) {
                setError("No scheduling suggestions available. This might be because Google Maps API is not configured or the address couldn't be found.");
            } else {
                setSuggestions(res.data.suggestions);
                setSuggestionsOpen(true);
            }
        } catch (err) {
            console.error(err);
            const detail = err.response?.data?.detail;
            if (detail) {
                setError(`Scheduling error: ${detail}`);
            } else {
                setError("Failed to get scheduling suggestions. Please check your internet connection and ensure the address is valid.");
            }
        }
        setSuggestionsLoading(false);
    };

    const handleSelectTime = (suggestion) => {
        // suggestion.start_time is ISO string
        const dt = parseUtc(suggestion.start_time);
        const date = dt.toISOString().split('T')[0];
        const time = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        
        setFormData({
            ...formData,
            scheduled_date: date,
            scheduled_time: time
        });
        setSuggestionsOpen(false);
    };

    const copyToClipboard = () => {
        if (magicLink) {
            navigator.clipboard.writeText(magicLink);
            setSuccess('Link copied to clipboard');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setLoading(true);

        try {
            // Combine date and time if both present
            let scheduled_date = null;
            if (formData.scheduled_date && formData.scheduled_time) {
                scheduled_date = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`).toISOString();
            } else if (formData.scheduled_date) {
                scheduled_date = new Date(formData.scheduled_date).toISOString();
            }

            // Terminal outcomes require a reason — the backend rejects without one
            if (formData.status === 'Failed' && !formData.failure_reason.trim()) {
                setError('A failure reason is required when marking a job as Failed.');
                setLoading(false);
                return;
            }
            if (formData.status === 'Cancelled' && !formData.cancellation_reason.trim()) {
                setError('A cancellation reason is required when marking a job as Cancelled.');
                setLoading(false);
                return;
            }

            const payload = {
                description: formData.description,
                location: formData.location,
                scheduled_date: scheduled_date,
                client_id: formData.client_id ? parseInt(formData.client_id) : null,
                status: formData.status,
                failure_reason: formData.status === 'Failed' ? formData.failure_reason : null,
                cancellation_reason: formData.status === 'Cancelled' ? formData.cancellation_reason : null,
                job_type: formData.job_type,
                urgency: formData.urgency,
                site_access: formData.site_access,
                site_contact_name: formData.site_contact_name,
                site_contact_phone: formData.site_contact_phone,
                bill_to_other: formData.bill_to_other,
                billing_name: formData.billing_name,
                billing_email: formData.billing_email,
                flexibility: formData.flexibility,
                not_before_date: formData.not_before_date || null,
                time_preference: formData.time_preference,
                special_instructions: formData.special_instructions,
                employee_id: formData.employee_id ? parseInt(formData.employee_id) : null
            };

            if (isEditMode) {
                await api.put(`/admin/jobs/${id}`, payload);
                setSuccess('Job updated successfully!');
            } else {
                await api.post('/admin/jobs', payload);
                setSuccess('Job created successfully!');
            }
            
            // Redirect after short delay
            setTimeout(() => navigate('/jobs'), 1500);
        } catch (err) {
            setError(err.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} job`);
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this job?')) return;
        try {
            await api.delete(`/admin/jobs/${id}`);
            navigate('/jobs');
        } catch (err) {
            setError('Failed to delete job');
        }
    };

    if (initialLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, minHeight: '60vh', alignItems: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 2, pb: 8 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton 
                        onClick={() => navigate(-1)} 
                        sx={{ mr: 1, color: 'text.secondary', bgcolor: 'white', border: '1px solid #E2E8F0' }}
                    >
                        <ArrowBack fontSize="small" />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight={800} color="text.primary">
                            {isEditMode ? 'Edit Job' : 'Create New Job'}
                        </Typography>
                        {isEditMode && (
                            <Typography variant="body2" color="text.secondary">
                                ID: #{id}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {isEditMode && (
                    <Button 
                        type="button"
                        startIcon={<Delete />} 
                        color="error" 
                        variant="outlined"
                        onClick={handleDelete}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        Delete
                    </Button>
                )}
            </Box>
            
            <Paper elevation={0} sx={{ p: 4, border: '1px solid #E2E8F0', borderRadius: 3 }}>
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
                {success && (
                    <Alert 
                        severity="success" 
                        icon={<CheckCircle fontSize="inherit" />}
                        sx={{ mb: 3, borderRadius: 2 }}
                    >
                        {success}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                                Job Details
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                label="Job Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="e.g. Soil Test for New Build"
                                variant="outlined"
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                label="Location / Address"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                placeholder="e.g. 123 Smith St, Suburb"
                                variant="outlined"
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>
                         <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Job Type</InputLabel>
                                <Select
                                    name="job_type"
                                    value={formData.job_type}
                                    label="Job Type"
                                    onChange={handleChange}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="standard">Standard Soil Test</MenuItem>
                                    <MenuItem value="detailed">Detailed Analysis</MenuItem>
                                    <MenuItem value="contamination">Contamination Screen</MenuItem>
                                    <MenuItem value="percolation">Percolation Test</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Urgency</InputLabel>
                                <Select
                                    name="urgency"
                                    value={formData.urgency}
                                    label="Urgency"
                                    onChange={handleChange}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="normal">Normal</MenuItem>
                                    <MenuItem value="high">High</MenuItem>
                                    <MenuItem value="urgent">Urgent</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                                Scheduling
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleSuggestTimes}
                                disabled={suggestionsLoading || !formData.location}
                                startIcon={suggestionsLoading ? <CircularProgress size={20} /> : <Star />}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                                {suggestionsLoading ? 'Analyzing Routes...' : 'Suggest Best Time'}
                            </Button>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Scheduled Date"
                                name="scheduled_date"
                                InputLabelProps={{ shrink: true }}
                                value={formData.scheduled_date}
                                onChange={handleChange}
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="time"
                                label="Scheduled Time"
                                name="scheduled_time"
                                InputLabelProps={{ shrink: true }}
                                value={formData.scheduled_time}
                                onChange={handleChange}
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>
                         <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    name="status"
                                    value={formData.status}
                                    label="Status"
                                    onChange={handleChange}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="New Request">New Request</MenuItem>
                                    <MenuItem value="Needs Info">Needs Info</MenuItem>
                                    <MenuItem value="Waiting on Client">Waiting on Client</MenuItem>
                                    <MenuItem value="Tentative">Tentative</MenuItem>
                                    <MenuItem value="Locked">Locked</MenuItem>
                                    <MenuItem value="Confirmed">Confirmed</MenuItem>
                                    <MenuItem value="En Route">En Route</MenuItem>
                                    <MenuItem value="On Site">On Site</MenuItem>
                                    <MenuItem value="Completed">Completed</MenuItem>
                                    <MenuItem value="Failed">Failed</MenuItem>
                                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {(formData.status === 'Failed' || formData.status === 'Cancelled') && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    label={formData.status === 'Failed' ? 'Failure Reason' : 'Cancellation Reason'}
                                    name={formData.status === 'Failed' ? 'failure_reason' : 'cancellation_reason'}
                                    value={formData.status === 'Failed' ? formData.failure_reason : formData.cancellation_reason}
                                    onChange={handleChange}
                                    placeholder={formData.status === 'Failed' ? 'e.g. No access, client not home, equipment issue' : 'e.g. Client cancelled, duplicate booking'}
                                    InputProps={{ sx: { borderRadius: 2 } }}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Client (Optional)</InputLabel>
                                <Select
                                    name="client_id"
                                    value={formData.client_id}
                                    label="Client (Optional)"
                                    onChange={handleChange}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {clients.map((client) => (
                                        <MenuItem key={client.id} value={client.id}>
                                            {client.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Assign Worker (Optional)</InputLabel>
                                <Select
                                    name="employee_id"
                                    value={formData.employee_id}
                                    label="Assign Worker (Optional)"
                                    onChange={handleChange}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                                    {employees.map((emp) => (
                                        <MenuItem key={emp.id} value={emp.id}>
                                            {emp.name} {emp.role ? `(${emp.role})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                                Site Access & Contact
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Access Type</InputLabel>
                                <Select
                                    name="site_access"
                                    value={formData.site_access}
                                    label="Access Type"
                                    onChange={handleChange}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="open">Open Access</MenuItem>
                                    <MenuItem value="locked">Key/Lockbox</MenuItem>
                                    <MenuItem value="arrange">Arrange with Contact</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                             <TextField
                                fullWidth
                                label="Site Contact Name"
                                name="site_contact_name"
                                value={formData.site_contact_name}
                                onChange={handleChange}
                                variant="outlined"
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                             <TextField
                                fullWidth
                                label="Site Contact Phone"
                                name="site_contact_phone"
                                value={formData.site_contact_phone}
                                onChange={handleChange}
                                variant="outlined"
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                                Scheduling Constraints
                            </Typography>
                        </Grid>
                         <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Flexibility</InputLabel>
                                <Select
                                    name="flexibility"
                                    value={formData.flexibility}
                                    label="Flexibility"
                                    onChange={handleChange}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="flexible">Flexible</MenuItem>
                                    <MenuItem value="needs_notice">Needs Notice</MenuItem>
                                    <MenuItem value="fixed_time">Fixed Time</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                             <TextField
                                fullWidth
                                type="date"
                                label="Not Before Date"
                                name="not_before_date"
                                InputLabelProps={{ shrink: true }}
                                value={formData.not_before_date}
                                onChange={handleChange}
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>
                         <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Time Preference</InputLabel>
                                <Select
                                    name="time_preference"
                                    value={formData.time_preference}
                                    label="Time Preference"
                                    onChange={handleChange}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="any">Any Time</MenuItem>
                                    <MenuItem value="morning">Morning</MenuItem>
                                    <MenuItem value="afternoon">Afternoon</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                         <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                                Billing Details
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                             <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <input
                                    type="checkbox"
                                    name="bill_to_other"
                                    checked={formData.bill_to_other}
                                    onChange={handleChange}
                                    style={{ width: 18, height: 18, marginRight: 8, accentColor: theme.palette.primary.main }}
                                />
                                <Typography fontWeight={500}>Bill to a different party (not the client)</Typography>
                             </Box>
                        </Grid>
                        {formData.bill_to_other && (
                            <>
                                <Grid item xs={12} sm={6}>
                                     <TextField
                                        fullWidth
                                        label="Billing Name"
                                        name="billing_name"
                                        value={formData.billing_name}
                                        onChange={handleChange}
                                        variant="outlined"
                                        InputProps={{ sx: { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                     <TextField
                                        fullWidth
                                        label="Billing Email"
                                        name="billing_email"
                                        value={formData.billing_email}
                                        onChange={handleChange}
                                        variant="outlined"
                                        InputProps={{ sx: { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </>
                        )}
                        
                        <Grid item xs={12}>
                             <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Special Instructions / Notes"
                                name="special_instructions"
                                value={formData.special_instructions}
                                onChange={handleChange}
                                variant="outlined"
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Button 
                                type="submit" 
                                variant="contained" 
                                size="large"
                                fullWidth
                                disabled={loading}
                                startIcon={!loading && <Save />}
                                sx={{ 
                                    py: 1.5,
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
                                    '&:hover': {
                                        boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
                                    }
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : (isEditMode ? 'Update Job' : 'Create Job')}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {/* Customer Portal Section (Edit Mode Only) */}
            {isEditMode && (
                <Paper elevation={0} sx={{ p: 4, mt: 3, border: '1px solid #E2E8F0', borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <LinkIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="h6" fontWeight={700}>Customer Portal Access</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Generate a secure, temporary link for the client to track this job's status without logging in.
                    </Typography>

                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={() => handleGenerateLink(false)}
                                disabled={linkLoading}
                                startIcon={linkLoading ? <CircularProgress size={20} /> : <LinkIcon />}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                                Generate Link Only
                            </Button>
                        </Grid>
                        {formData.client_id && (
                            <Grid item>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={() => handleGenerateLink(true)}
                                    disabled={linkLoading}
                                    startIcon={linkLoading ? <CircularProgress size={20} /> : <Email />}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                >
                                    Generate & Email Client
                                </Button>
                            </Grid>
                        )}
                    </Grid>

                    {magicLink && (
                        <Box sx={{ mt: 3, p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px dashed #E2E8F0' }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>
                                MAGIC LINK (EXPIRES IN 7 DAYS)
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={magicLink}
                                    InputProps={{
                                        readOnly: true,
                                        sx: { bgcolor: 'white' }
                                    }}
                                />
                                <IconButton onClick={copyToClipboard} sx={{ ml: 1 }}>
                                    <ContentCopy />
                                </IconButton>
                            </Box>
                        </Box>
                    )}
                </Paper>
            )}

            {/* Suggestions Dialog */}
            <Dialog 
                open={suggestionsOpen} 
                onClose={() => setSuggestionsOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    Suggested Times
                </DialogTitle>
                <DialogContent dividers>
                    {suggestions.length === 0 ? (
                        <Typography color="text.secondary" align="center" py={3}>
                            No optimal times found in the next 14 days.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {suggestions.map((s, idx) => {
                                const dt = parseUtc(s.start_time);
                                return (
                                    <React.Fragment key={idx}>
                                        <ListItem disablePadding>
                                            <ListItemButton 
                                                onClick={() => handleSelectTime(s)}
                                                sx={{ borderRadius: 2, mb: 1, border: '1px solid #E2E8F0' }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography fontWeight={600}>
                                                                {dt.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                                                            </Typography>
                                                            <Chip 
                                                                label={s.score > 0.8 ? "High Fit" : "Medium Fit"} 
                                                                color={s.score > 0.8 ? "success" : "warning"}
                                                                size="small"
                                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                                            />
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <React.Fragment>
                                                            <Typography variant="body2" color="text.primary" sx={{ my: 0.5 }}>
                                                                {dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {s.reason}
                                                            </Typography>
                                                        </React.Fragment>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default JobForm;
